import { Button } from "@/components/ui/button";
import { GOOGLE_SSO_START_PATH } from "@/lib/api";
import { logger } from "@/lib/logger";

interface GoogleSsoButtonProps {
  /**
   * The page to land on after sign-in. Pass only a value that passes
   * `isSafeRelativePath`; the backend checks it again against the user's side.
   */
  next?: string;
}

/**
 * "Continue with Google" for BOI employees, with its caption.
 *
 * Clicking leaves the SPA with a full navigation to the backend's start
 * endpoint, which redirects through Supabase to Google and back to the
 * backend's callback. The SPA never sees a Supabase token: the callback sets
 * the HTTP-only session cookie and lands on `next` when it is on the user's
 * side, or on `/cockpit`. Staff sessions always last 7 days, so "Keep me
 * signed in" (password form) does not apply here.
 */
export function GoogleSsoButton({ next }: GoogleSsoButtonProps) {
  const start = () => {
    logger.debug("[auth] starting Google SSO, return to:", next ?? "(role home)");
    window.location.assign(
      next ? `${GOOGLE_SSO_START_PATH}?next=${encodeURIComponent(next)}` : GOOGLE_SSO_START_PATH,
    );
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        onClick={start}
        className="h-11 w-full rounded-full text-sm font-medium"
      >
        <GoogleLogo />
        Continue with Google
      </Button>
      <p className="text-muted text-center text-xs leading-5">
        For Board of Innovation employees: use your BOI Google account.
      </p>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.02-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.02 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
